import { type SlAlert } from '@shoelace-style/shoelace';
import { type IntlService } from 'ember-intl';
import { convertToCsv } from 'dashboard/utils/convert-to-csv';
import { isPending, isDeactivated, isSynced } from 'dashboard/utils';
import type { CourseMember } from 'dashboard/types';
import type ToastService from 'dashboard/services/toast';

/* number of users to fetch at a time (to not overload service or prevent
   a malformed JSON response) */
export const EXPORT_PAGE_SIZE = 150;

// match the default order of the student and teacher lists so exports match
export const EXPORT_DEFAULT_ORDER_BY = 'accountStatus-asc, lastName';

/** A checkbox option in an export modal (matches `ListFilter.options`). */
export type ExportFilterOption = {
  name: string;
  value: string;
  checked: boolean;
};

/** The paginated connection shape returned by the list queries. */
export type ExportConnection<T> = {
  edges: Array<{ node: T }>;
  pageInfo: { hasNextPage: boolean; endCursor: string };
  queryResultCount: number;
};

/**
 * Fetch page-by-page until there are no more pages, showing a progress toast
 * that updates on each successful query. Closing the toast cancels the export.
 *
 * @returns every fetched item, or `undefined` if the export was cancelled.
 */
export async function fetchAllExportPages<T>({
  toast,
  fetchPage,
  messages,
}: {
  toast: ToastService;
  fetchPage: (after: string) => Promise<ExportConnection<T>>;
  messages: {
    start: string;
    progress: (count: number, total: number) => string;
    error: string;
  };
}): Promise<T[] | undefined> {
  const allItems: T[] = [];
  let cancelled = false;

  // show the toast right away - it will update on each successful query
  const progressAlert = createExportToast(messages.start);

  // listen for closing alert to cancel fetch
  progressAlert.addEventListener(
    'sl-hide',
    () => {
      cancelled = true;
    },
    { once: true },
  );

  try {
    let after = '';
    let hasNextPage = true;
    let total: number | undefined;

    // only loop if there are more pages to fetch and they haven't cancelled
    while (hasNextPage && !cancelled) {
      const connection = await fetchPage(after);

      if (cancelled) break;
      if (!total) total = connection.queryResultCount;

      allItems.push(...connection.edges.map((edge) => edge.node));
      hasNextPage = connection.pageInfo.hasNextPage;
      after = connection.pageInfo.endCursor;

      updateExportToastMessage(
        progressAlert,
        messages.progress(allItems.length, total),
      );
    }
  } catch (reason) {
    // TODO: possible to retry with smaller EXPORT_PAGE_SIZE ?
    // (especially if a JSON error is the captured reason?)
    if (cancelled) return;
    progressAlert.hide();
    toast.warn(reason, messages.error);
    return Promise.reject(reason);
  }

  if (cancelled) return;

  progressAlert.hide();

  return allItems;
}

/**
 * Convert rows to a CSV and show a success toast with a download link.
 * `filename` and `label` are inserted as HTML, so escape any user content.
 */
export function showCsvDownloadToast(
  rows: string[][],
  filename: string,
  label: string,
) {
  const csvUrl = convertToCsv(rows);
  const reportLink = `<a href="${csvUrl}" download="${filename}">${label}</a>`;

  const successAlert = createExportToast(reportLink, 'success');

  // the CSV only needs to live as long as the toast does; free it once the
  // user is done with the download link (or dismisses the toast)
  successAlert.addEventListener(
    'sl-after-hide',
    () => URL.revokeObjectURL(csvUrl),
    { once: true },
  );
}

/** Status column text, matching `<StatusLabel>`'s accessible label. */
export function exportStatusLabel(intl: IntlService, user: CourseMember) {
  if (isPending(user)) return intl.t('Pending');
  if (isDeactivated(user)) return intl.t('Deactivated');

  return isSynced(user) ? intl.t('Synced member') : intl.t('Member');
}

/** Last active column text; blank for pending users. */
export function exportLastActive(intl: IntlService, user: CourseMember) {
  return !isPending(user) && user.lastUserAccess
    ? intl.formatDate(new Date(user.lastUserAccess), { format: 'dmy' })
    : '';
}

/**
 * Build a toast alert with an icon (matching the look of the QR export's
 * toasts), append it to the DOM, and show it. The message lives in its own
 * element so it can be swapped out later via `updateExportToastMessage`
 * without touching (and thereby restarting) the icon/spinner.
 */
function createExportToast(
  message: string,
  state: 'progress' | 'success' = 'progress',
): SlAlert {
  const icon =
    state === 'success'
      ? `<sl-icon library="showbie" name="check" slot="icon" class="w-8 h-8 text-green-600"></sl-icon>`
      : `<sl-spinner slot="icon"></sl-spinner>`;

  const alert = Object.assign(document.createElement('sl-alert'), {
    variant: 'primary',
    closable: true,
  });

  alert.innerHTML = `${icon} <span data-toast-message>${message}</span>`;
  alert.classList.add('lbx-toast');

  document.body.append(alert);
  alert.toast();

  return alert;
}

function updateExportToastMessage(alert: SlAlert, message: string) {
  const messageEl = alert.querySelector('[data-toast-message]');
  if (messageEl) messageEl.textContent = message;
}
